import * as React from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Modal } from '@design-system/components/modal/modal';
import { Input } from '@design-system/components/input/input';
import { sendEmailChallenge } from '@client/new/api/auth.api';

export type EmailPromptFormValues = { email: string };

const buildSchema = (currentEmail: string | null) =>
  z.object({
    email: z
      .string()
      .trim()
      .nonempty('Please enter an email address')
      .email('Please enter a valid email address')
      .refine(
        (value) =>
          !currentEmail ||
          value.toLowerCase() !== currentEmail.trim().toLowerCase(),
        { message: 'Email is the same as the current one' },
      ),
  });

type Props = {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string | null;
  title: string;
  description: string;
  onSubmit: (formValues: EmailPromptFormValues) => void;
};

export const EmailPromptModal = ({
  isOpen,
  onClose,
  currentEmail,
  title,
  description,
  onSubmit,
}: Props) => {
  const schema = React.useMemo(() => buildSchema(currentEmail), [currentEmail]);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<EmailPromptFormValues>({
    defaultValues: { email: '' },
    resolver: zodResolver(schema),
  });

  React.useEffect(() => {
    if (!isOpen) reset({ email: '' });
  }, [isOpen, reset]);

  const submit = handleSubmit(async (formValues) => {
    try {
      await sendEmailChallenge(formValues.email, 'update-signin-email');
      onSubmit(formValues);
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status === 409) {
        setError('email', { message: 'Email already in use' });
        return;
      }
      toast.error('Failed to send verification code');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      centerContent
      primaryCta={{
        caption: 'Send code',
        onClick: submit,
        disabled: isSubmitting,
      }}
      secondaryCta={{ caption: 'Cancel', onClick: onClose }}
      modalClassName="w-full md:w-[600px]"
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-base text-black text-center">{description}</p>
        <Input
          type="email"
          placeholder="Email address"
          error={errors.email?.message}
          disabled={isSubmitting}
          {...register('email')}
        />
      </div>
    </Modal>
  );
};
